import { useState, useEffect, useRef } from 'react';
import { Box, CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, Select, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@qwickapps/auth-client';
import { useMiddlewareClient, type Device } from '@qwickforge/react-client';
import { Button, Text } from '@qwickapps/react-framework';
import AddIcon from '@mui/icons-material/Add';
import ChatInterface from '../components/ChatInterface';
import SessionSelector from '../components/SessionSelector';
import DeviceStatus from '../components/DeviceStatus';

interface Project {
  id: string;
  name: string;
  updatedAt: string;
}

interface Session {
  id: string;
  projectId: string;
  deviceId?: string;
  title: string;
  updatedAt?: string;
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const client = useMiddlewareClient();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [sessionError, setSessionError] = useState<string>('');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [activeDeviceId, setActiveDeviceId] = useState<string | null>(null);
  const [availableDevices, setAvailableDevices] = useState<Device[]>([]);
  const [showDeviceSelector, setShowDeviceSelector] = useState(false);
  const [pendingSessionType, setPendingSessionType] = useState<'auto' | 'new' | null>(null);

  // Prevent duplicate requests from React StrictMode double-rendering
  const loadingRef = useRef(false);
  const initialLoadDoneRef = useRef(false);

  useEffect(() => {
    // Only load on mount, not when session refreshes on focus
    if (!loadingRef.current && !initialLoadDoneRef.current && session?.accessToken) {
      loadingRef.current = true;
      loadProjectAndSession().finally(() => {
        loadingRef.current = false;
        initialLoadDoneRef.current = true;
      });
    }
  }, [session?.accessToken]); // Only depend on accessToken existence, not the full session object

  const loadProjectAndSession = async () => {
    try {
      setLoading(true);
      setError('');

      if (!session?.accessToken || !client) {
        setError('Not authenticated');
        return;
      }

      // Check for active devices first
      const devicesData = await client.devices.list();
      const devices = Array.isArray(devicesData) ? devicesData : devicesData.devices || [];
      const activeDevices = devices.filter(d => !d.deletedAt);

      if (activeDevices.length === 0) {
        // No active devices - redirect to devices page
        navigate('/devices');
        return;
      }

      // Store available devices
      setAvailableDevices(activeDevices);

      // Only auto-select device if there's exactly one
      if (activeDevices.length === 1) {
        setActiveDeviceId(activeDevices[0].id);
      } else {
        // Multiple devices - will prompt user to select when creating a session
        setActiveDeviceId(null);
      }

      // Fetch projects
      const projects: Project[] = await client.projects.list();

      if (projects.length === 0) {
        // No projects found - this shouldn't happen as user provisioning creates a default project
        // But if it does, show an error instead of trying to create one
        setError('No projects found. Please contact support.');
        return;
      }

      // Auto-select project (single project or last updated)
      const project = projects.length === 1
        ? projects[0]
        : projects.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0];

      setSelectedProject(project);
      await loadSessionsForProject(project.id);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadSessionsForProject = async (projectId: string) => {
    if (!session?.accessToken || !client) return;

    try {
      // Fetch sessions - list all and filter by projectId
      const allSessions: any = await client.sessions.list(undefined, 50, 0);

      // Handle both array and paginated response formats
      const sessionList = Array.isArray(allSessions) ? allSessions : (allSessions.sessions || allSessions.data || []);
      const sessions = sessionList.filter((s: any) => s.projectId === projectId);

      if (sessions.length > 0) {
        // Auto-select last session
        const lastSession = sessions.sort((a, b) =>
          new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime()
        )[0];
        setSelectedSession(lastSession);
      } else {
        // No sessions found - need to create one
        if (activeDeviceId) {
          // Device already selected (single device case) - create session automatically
          const newSession = await client.sessions.create(
            `Session ${new Date().toLocaleString()}`,
            {},
            projectId,
            activeDeviceId
          );
          setSelectedSession(newSession);
        } else {
          // Multiple devices available - prompt user to select a device first
          setPendingSessionType('auto');
          setShowDeviceSelector(true);
        }
      }
    } catch (err) {
      console.error('Failed to load sessions:', err);
    }
  };

  const handleNewSession = async () => {
    if (!selectedProject || !session?.accessToken || !client) return;

    // If no device selected and multiple devices available, show selector
    if (!activeDeviceId && availableDevices.length > 1) {
      setPendingSessionType('new');
      setShowDeviceSelector(true);
      return;
    }

    try {
      setSessionError('');
      const newSession = await client.sessions.create(
        `Session ${new Date().toLocaleString()}`,
        {},
        selectedProject.id,
        activeDeviceId || undefined
      );
      setSelectedSession(newSession);
    } catch (error) {
      console.error('Failed to create session:', error);
      setSessionError(error instanceof Error ? error.message : 'Failed to create session');
    }
  };

  const handleDeviceSelected = async (deviceId: string) => {
    if (!selectedProject || !session?.accessToken || !client) return;

    setActiveDeviceId(deviceId);
    setShowDeviceSelector(false);

    // Create the pending session with the selected device
    try {
      setSessionError('');
      const newSession = await client.sessions.create(
        `Session ${new Date().toLocaleString()}`,
        {},
        selectedProject.id,
        deviceId
      );
      setSelectedSession(newSession);
      setPendingSessionType(null);
    } catch (error) {
      console.error('Failed to create session:', error);
      setSessionError(error instanceof Error ? error.message : 'Failed to create session');
    }
  };


  if (loading) {
    return (
      <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 8, maxWidth: 800, mx: 'auto' }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{
      height: 'calc(100vh - 100px)',
      display: 'flex',
      flexDirection: 'column',
      maxWidth: 1600,
      mx: 'auto',
      px: 3
    }}>
      {/* Error Alert */}
      {sessionError && (
        <Alert severity="error" onClose={() => setSessionError('')} sx={{ mb: 2 }}>
          {sessionError}
        </Alert>
      )}

      <Box sx={{ py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {/* Header with project info */}
        <Box>
          <Text variant="h5" gutterBottom customColor="var(--theme-text-primary)" content={selectedProject?.name || 'QwickForge AI Assistant'} />
        </Box>

        {/* Controls: Session Title/Selector, Device Status, New Session Button */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Session Title/Selector - Unified component */}
          <SessionSelector
            projectId={selectedProject?.id}
            selectedSession={selectedSession}
            onSessionChange={(session) => setSelectedSession(session)}
            onError={(error) => setSessionError(error)}
          />

          {/* Device Status - read-only view showing device connectivity */}
          <DeviceStatus deviceId={selectedSession?.deviceId} />

          {/* New Session Button */}
          <Button
            size="large"
            icon={<AddIcon />}
            onClick={handleNewSession}
            disabled={!selectedProject}
          >
            New Session
          </Button>
        </Box>
      </Box>

      {/* Chat Interface - Primary View */}
      <Box sx={{ flex: 1, minHeight: 0, pb: 2 }}>
        <ChatInterface
          projectId={selectedProject?.id}
          sessionId={selectedSession?.id}
        />
      </Box>

      {/* Device Selection Dialog */}
      <Dialog open={showDeviceSelector} onClose={() => setShowDeviceSelector(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Text variant="h6" content="Select a Device" />
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Text
              variant="body2"
              content="Multiple devices are available. Please select which device should run this session:"
              customColor="var(--theme-text-secondary)"
              gutterBottom
            />
            <FormControl fullWidth sx={{ mt: 2 }}>
              <Select
                value={activeDeviceId || ''}
                onChange={(e) => setActiveDeviceId(e.target.value)}
                displayEmpty
                sx={{
                  backgroundColor: 'var(--theme-surface)',
                  color: 'var(--theme-text)',
                }}
              >
                <MenuItem value="" disabled>
                  <Text variant="body2" content="Choose a device..." customColor="var(--theme-text-disabled)" />
                </MenuItem>
                {availableDevices.map((device) => (
                  <MenuItem key={device.id} value={device.id}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Text variant="body2" content={device.name || device.hostname || 'Unknown Device'} />
                      {device.hostname && device.name && (
                        <Text variant="caption" content={device.hostname} customColor="var(--theme-text-secondary)" />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeviceSelector(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => activeDeviceId && handleDeviceSelected(activeDeviceId)}
            disabled={!activeDeviceId}
          >
            Select Device
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
